import {
  useQuery,
  useMutation,
  queryOptions,
  mutationOptions,
} from "@tanstack/react-query";

import {
  uploadFileByURL,
  uploadFileByBlob,
  fetchPresignURL,
} from "@app/services/s3";

import { downloadImage } from "@app/services/downloadFile";

export const s3Key = {
  all: ["s3"],
  presignURL: (filePath) => [...s3Key.all, "presignURL", filePath],
};

export const uploadFileMutationOptions = (options = {}) =>
  mutationOptions({
    mutationFn: ({ filePath, fileURL, fileBlob }, { signal }) => {
      if (fileBlob) {
        return uploadFileByBlob(filePath, fileBlob, { signal });
      }

      if (fileURL) {
        return uploadFileByURL(filePath, fileURL, { signal });
      }

      throw new Error("fileURL or fileBlob is required for upload");
    },
    onSuccess: (data, variables, context, { client }) => {
      client.setQueryData(s3Key.presignURL(data.filePath), data);
    },
    meta: {
      errorMessage: "Failed to upload file. Please try again.",
    },
    ...options,
  });

export const useUploadFile = (options = {}) => {
  return useMutation(uploadFileMutationOptions(options));
};

export const presignURLQueryOptions = (filePath, options = {}) =>
  queryOptions({
    queryKey: s3Key.presignURL(filePath),
    queryFn: ({ signal }) => fetchPresignURL(filePath, { signal }),
    enabled: !!filePath,
    staleTime: 1000 * 60 * 20, // 20 minutes
    gcTime: 1000 * 60 * 50, // 50 minutes
    ...options,
  });

/**
 * Presign one file — or, when `filePath` is an array, several at once. A batch
 * resolves to `data.urls[]`, each entry a `{ filePath, presignURL }` pair.
 */
export const usePresignURL = (filePath, options = {}) => {
  return useQuery(presignURLQueryOptions(filePath, options));
};

export const useDownloadS3Image = (options = {}) => {
  return useMutation({
    mutationFn: async ({ filePath, fileName }, { signal, client }) => {
      const s3Data = await client.query(presignURLQueryOptions(filePath));

      return downloadImage({ url: s3Data.presignURL }, fileName, { signal });
    },
    meta: {
      errorMessage: "Failed to download image. Please try again.",
    },
    ...options,
  });
};
